import { ButtonIcon, Icon, Typography } from "@saviynt/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IntegrationTypeCategory,
  useIntegrationTypes,
} from "../../hooks/useIntegrationTypes";
import ModalFullPage from "../ModalFullPage";
import { IntegrationTypeCard } from "./IntegrationTypeCard";
import { IntegrationTypeCardSkeleton } from "./IntegrationTypeCardSkeleton";
import "./IntegrationTypeCardsModal.css";

interface IntegrationTypeCardsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectType: (typeName: string) => void;
  category?: IntegrationTypeCategory;
}

const SKELETON_COUNT = 6;

export function IntegrationTypeCardsModal({
  isOpen,
  onClose,
  onSelectType,
  category,
}: IntegrationTypeCardsModalProps) {
  const intl = useIntl();
  const {
    data: integrationTypes,
    isLoading,
    error,
    refetch,
  } = useIntegrationTypes({ enabled: isOpen, category });

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, category, refetch]);

  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset + autofocus when modal opens.
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  // "/" shortcut to refocus search when modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/") return;
      const active = document.activeElement;
      const isTyping =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (isTyping) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  const enabledTypes = useMemo(
    () => (integrationTypes ?? []).filter((t) => t.isEnabled),
    [integrationTypes],
  );

  const filteredTypes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enabledTypes;
    return enabledTypes.filter((t) => {
      return (
        t.displayName.toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        t.typeName.toLowerCase().includes(q)
      );
    });
  }, [enabledTypes, query]);

  const searchPlaceholder = intl.formatMessage({
    id: "GRGOWL",
    defaultMessage: "Search integrations",
    description: "Placeholder for the integration type search input",
  });

  return (
    <ModalFullPage
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      title={intl.formatMessage({
        id: "YYujKG",
        defaultMessage: "Add Integration",
        description: "Page title for add integration form",
      })}
    >
      <div className="IntegrationTypeCardsModal w-full max-w-6xl mx-auto px-6 py-8">
        {/* Header row — subtitle + search */}
        <div className="flex flex-col gap-4 mb-6">
          <p className="text-[var(--color-foreground-neutral-medium,#4a5568)] text-sm m-0">
            <FormattedMessage
              id="/mmCn2"
              defaultMessage="Choose a service to integrate with Saviynt."
              description="Subtitle above the integration type cards"
            />
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <label className="IntegrationTypeCardsModal__search relative flex-1 max-w-md">
              <span className="IntegrationTypeCardsModal__searchIcon">
                <Icon kind="Search" size="small" />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="IntegrationTypeCardsModal__searchInput w-full rounded-[var(--border-radius-sm,8px)] border pl-9 pr-9 py-2 text-sm focus:outline-none"
              />
              {query && (
                <ButtonIcon
                  kind="ghost"
                  size="small"
                  icon={<Icon kind="Close" size="small" />}
                  onClick={() => {
                    setQuery("");
                    searchInputRef.current?.focus();
                  }}
                  ariaLabel={intl.formatMessage({
                    id: "CjBHYs",
                    defaultMessage: "Clear search",
                    description: "Aria label for clear-search button",
                  })}
                  className="IntegrationTypeCardsModal__clearBtn"
                />
              )}
            </label>

            {!isLoading && !error && enabledTypes.length > 0 && (
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle,#6b7280)] shrink-0"
              >
                <FormattedMessage
                  id="mIof3v"
                  defaultMessage="Showing {shown} of {total}"
                  description="Visible/total integration type count"
                  values={{
                    shown: filteredTypes.length,
                    total: enabledTypes.length,
                  }}
                />
              </Typography>
            )}
          </div>
        </div>

        {/* Loading — skeleton grid */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <IntegrationTypeCardSkeleton key={i} index={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="text-center py-12 text-[var(--color-foreground-critical-bold,#b3261e)]">
            <FormattedMessage
              id="+Jl9a3"
              defaultMessage="Failed to load integration types"
              description="Error message when integration types fail to load"
            />
          </div>
        )}

        {/* Empty search state */}
        {!isLoading &&
          !error &&
          enabledTypes.length > 0 &&
          filteredTypes.length === 0 && (
            <div className="IntegrationTypeCardsModal__empty flex flex-col items-center justify-center text-center py-16 gap-3">
              <div className="IntegrationTypeCardsModal__emptyIcon flex items-center justify-center rounded-full">
                <Icon kind="Search" size="medium" />
              </div>
              <Typography
                kind="h4"
                className="text-[var(--color-foreground-neutral-bold,#0d0f1c)]"
              >
                <FormattedMessage
                  id="sL8C52"
                  defaultMessage='No integrations match "{query}"'
                  description="Empty state title when search yields no results"
                  values={{ query }}
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-medium,#4a5568)] max-w-sm"
              >
                <FormattedMessage
                  id="gpY4tk"
                  defaultMessage="Try a different search term or clear the filter to see all integrations."
                  description="Empty state hint when search yields no results"
                />
              </Typography>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  searchInputRef.current?.focus();
                }}
                className="IntegrationTypeCardsModal__clearLink mt-2 inline-flex items-center gap-1 text-sm font-medium"
              >
                <FormattedMessage
                  id="eBDBlE"
                  defaultMessage="Clear search"
                  description="Button to clear the search query"
                />
              </button>
            </div>
          )}

        {/* Results */}
        {!isLoading && !error && filteredTypes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTypes.map((type, i) => (
              <IntegrationTypeCard
                key={type.id || type.typeName}
                type={type}
                onSelect={onSelectType}
                searchQuery={query}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </ModalFullPage>
  );
}
